/**
 * Coordinator Initialization
 *
 * Initializes all coordinators and registers their message handlers with the MessageRouter.
 * This is the single entry point for the new coordinator-based architecture.
 *
 * Called at app startup when USE_MESSAGE_ROUTER feature flag is enabled.
 */

import { PavilionCoordinator } from './pavilion/PavilionCoordinator';
import { NonsoleSanitizationMonitor } from './pavilion/NonsoleSanitizationMonitor';
import { GameLaunchCoordinator } from './game-launch/GameLaunchCoordinator';
import { SessionCoordinator } from './session/SessionCoordinator';
import { PeerCoordinator } from './peer/PeerCoordinator';
import { MediaCoordinator } from './media/MediaCoordinator';
import { StatefulMacroCoordinator } from './macro/StatefulMacroCoordinator';
import { ScheduledScreenshotWatcher } from './scheduled-watcher/ScheduledScreenshotWatcher';
import { PeerLiteConnectionFactory } from '@playcastdotio/PeerCore';
import { AppCoordinator } from './app/AppCoordinator';
import { realtimeMessageRouter, playjectorMessageRouter } from '../services/MessageRouter';
import { replicationRouter } from './ReplicationRouter';
import { authState, pauseInputState, machineIdState, loginProfileState, driverState, sanitizationState, hostState } from '../api/stores/shared'; // TODO-STRANGLER-FIG: Temporary legacy adapter
import { DisplaySourceManager } from './media/managers/DisplaySourceManager';
import type { RestartManager } from './restart/RestartManager';
import { initRestartManager } from './restart/restartInit';
import { AudioManager } from './media/managers/AudioManager';
import { DisplayOutputManager } from './media/managers/DisplayOutputManager';
import { serviceContainer } from '../services/ServiceContainer';
import { RealtimeSignalingService } from '../services/peer/RealtimeSignalingService';
import { getStateStore } from '../api/stores';
import { getElectronStores } from '../api/stores/electronStores';
import { generators, websocket, HostMessageType } from '@playcastdotio/Messaging';
import { InputManager } from './peer/managers/InputManager';
import { CampaignInputManager } from './peer/managers/CampaignInputManager';
import { GamepadManager } from './peer/managers/GamepadManager';
import { ActivityTracker } from './peer/managers/ActivityTracker';
import { AdminPeerHandler } from './peer/handlers/AdminPeerHandler';
import { RecordingControlManager } from '../services/playjector/managers/RecordingControlManager';
import { BroadcastStatusIpcSyncManager } from './session/managers/BroadcastStatusIpcSyncManager';
import { ConnectedUsersIpcSyncManager } from './session/managers/ConnectedUsersIpcSyncManager';
import { createInputConfigStores } from './peer/stores/inputConfigStores';
import {
  USE_MESSAGE_ROUTER,
  USE_PAVILION_COORDINATOR,
  USE_SESSION_COORDINATOR,
  USE_PLAYJECTOR_COORDINATOR,
  USE_MEDIA_COORDINATOR,
  USE_BEACON_COORDINATOR,
  USE_PROVISIONING_COORDINATOR,
} from '../config/featureFlags';
import { BeaconCoordinator } from './beacon/BeaconCoordinator';
import { ProvisioningCoordinator } from './provisioning/ProvisioningCoordinator';
import { NonsoleExitCoordinator } from './nonsole/NonsoleExitCoordinator';
import { wireScheduledWatcherEvents } from './scheduled-watcher/watcherEventWiring';
import { GamingScoreCoordinator } from './gamingScore/GamingScoreCoordinator';
import constants from '@playcastdotio/GlobalConfig';
import type { GamingScorePersistedData } from '@playcastdotio/Messaging';
import { setLegacyMatchmakerShadowcamHealthSource } from '../api/modules/managers/matchmaker';
import { initializeStreamTuning } from './streamTuning/initializeStreamTuning';

// Create coordinator singletons here (lazy, avoids GlobalConfig in tests)
// appCoordinator must be first — GameLaunchCoordinator injects it into GameLifecycleManager (avoids init↔game-launch cycle)
export const appCoordinator = new AppCoordinator(serviceContainer.analyticsManager);
export const pavilionCoordinator = new PavilionCoordinator(
  serviceContainer.realtimeService,
  serviceContainer.campaignConfigService,
  serviceContainer.analyticsManager
);
export const gameLaunchCoordinator = new GameLaunchCoordinator(
  serviceContainer.playjectorService,
  appCoordinator,
  serviceContainer.analyticsManager,
  USE_MESSAGE_ROUTER && USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR ? serviceContainer.gameSaveManager : undefined,
  USE_MESSAGE_ROUTER && USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR ? serviceContainer.gameSaveConfigurationAvailability : undefined,
  USE_MESSAGE_ROUTER && USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR ? serviceContainer.campaignGameSaveConfiguration : undefined,
);
export const mediaCoordinator = new MediaCoordinator(() => driverState.currentState);
export const realtimeSignalingService = new RealtimeSignalingService();
export const peerCoordinator = new PeerCoordinator(new PeerLiteConnectionFactory());
export const sessionCoordinator = new SessionCoordinator(
  serviceContainer.realtimeService,
  serviceContainer.peerManager,
  serviceContainer.analyticsManager
);

// StatefulMacroCoordinator (singleton — owns retryCount across stopGame cycles)
// Phase 3 of docs/feature-plans/stateful-macro-system. Wired in
// initializeCoordinators after gameLaunchCoordinator.init() makes its
// MacroManager available, since the macro coordinator depends on it.
export let statefulMacroCoordinator: StatefulMacroCoordinator | null = null;

// ScheduledScreenshotWatcher (singleton — periodic idle-screen health check)
export let scheduledScreenshotWatcher: ScheduledScreenshotWatcher | null = null;
async function fetchGamingScore(machineId: string): Promise<GamingScorePersistedData | null> {
  const res = await fetch(
    `${constants.REALTIME_API_BASE_URL}realtime/machines/${encodeURIComponent(machineId)}/gaming-score`,
  );

  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    throw new Error(`Failed to fetch gaming score (${res.status})`);
  }

  return (await res.json()) as GamingScorePersistedData;
}

export const gamingScoreCoordinator = new GamingScoreCoordinator(
  serviceContainer.realtimeService,
  () => machineIdState.currentState || null,
  fetchGamingScore,
);

// Initialized lazily inside initializeCoordinators when USE_MEDIA_COORDINATOR is true
export let adminPeerHandler: AdminPeerHandler | null = null;

// Beacon coordinator singleton (conditional on USE_BEACON_COORDINATOR)
export const beaconCoordinator: BeaconCoordinator | null = USE_BEACON_COORDINATOR
  ? new BeaconCoordinator({
      realtimeService: serviceContainer.realtimeService,
      getMachineId: () => machineIdState.currentState,
    })
  : null;

// Provisioning coordinator singleton (conditional on USE_PROVISIONING_COORDINATOR)
export const provisioningCoordinator: ProvisioningCoordinator | null = USE_PROVISIONING_COORDINATOR
  ? new ProvisioningCoordinator({
      ipcService: serviceContainer.ipcService,
      realtimeService: serviceContainer.realtimeService,
      getMachineId: () => machineIdState.currentState,
      getHostId: () => appCoordinator.getHostAuthId(),
    })
  : null;

export const nonsoleExitCoordinator = new NonsoleExitCoordinator({
  ipcService: serviceContainer.ipcService,
  getHostId: () => appCoordinator.getHostAuthId(),
});

// Beacon manager singletons (conditional on USE_BEACON_COORDINATOR)
export let displaySourceManager: DisplaySourceManager | null = null;
export let audioManager: AudioManager | null = null;
export let displayOutputManager: DisplayOutputManager | null = null;

// Restart manager singleton (always active)
export let restartManager: RestartManager = null!;

/**
 * Initialize all coordinators
 *
 * Call this once at app startup (before any messages are processed).
 */
export async function initializeCoordinators(): Promise<void> {
  if (!USE_MESSAGE_ROUTER) {
    console.log('[Coordinators] Feature flag disabled, skipping initialization');
    return;
  }

  console.log('[Coordinators] Initializing coordinator-based architecture...');

  try {
    // Initialize AppCoordinator FIRST (calculates static host properties)
    // All other coordinators will read from this single source of truth
    await appCoordinator.init();
    const legacyStoreForGamepads = getStateStore('ipc');
    appCoordinator.registerHostGamepadStore(legacyStoreForGamepads.hostGamepadState);
    console.log('[Coordinators] ✓ AppCoordinator initialized');

    // Initialize ProvisioningCoordinator (pavilion-only, independent of other coordinators)
    if (USE_PROVISIONING_COORDINATOR && provisioningCoordinator) {
      provisioningCoordinator.init();
      provisioningCoordinator.registerMessageHandlers(realtimeMessageRouter);
      provisioningCoordinator.registerPlayjectorHandlers(playjectorMessageRouter);
      console.log('[Coordinators] ✓ ProvisioningCoordinator initialized');
    } else {
      console.log('[Coordinators] 🚧 ProvisioningCoordinator skipped (feature flag disabled)');
    }

    if (USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR) {
      serviceContainer.initializeGameSaveStorage(realtimeMessageRouter);
    }

    // Initialize GameLaunchCoordinator (if enabled)
    // This must happen before PavilionCoordinator init so PavilionCoordinator can listen to its events
    if (USE_PLAYJECTOR_COORDINATOR) {
      await gameLaunchCoordinator.init(pavilionCoordinator, sessionCoordinator);
      gameLaunchCoordinator.registerReplicationHandler(replicationRouter);
      console.log('[Coordinators] ✓ GameLaunchCoordinator initialized');
    } else {
      console.log('[Coordinators] 🚧 GameLaunchCoordinator skipped (feature flag disabled)');
    }

    // Initialize SessionCoordinator (manages presence reporting)
    // Pass appCoordinator to read static properties
    // Pass gameLaunchCoordinator and peerCoordinator so it can listen to their events
    // Must be initialized BEFORE PeerCoordinator so PeerCoordinator can listen to streamStopped events
    if (USE_SESSION_COORDINATOR) {
      await sessionCoordinator.init(
        appCoordinator,
        USE_PLAYJECTOR_COORDINATOR ? gameLaunchCoordinator : undefined,
        peerCoordinator
      );
      console.log('[Coordinators] ✓ SessionCoordinator initialized');
      sessionCoordinator.events.joinableChanged.addListener(({ joinable }) => {
        hostState.updateState({
          ...hostState.currentState,
          streamJoinable: joinable,
        });
      });
    } else {
      console.log('[Coordinators] 🚧 SessionCoordinator skipped (feature flag disabled)');
    }

    gamingScoreCoordinator.init();
    console.log('[Coordinators] ✓ GamingScoreCoordinator initialized');

    if (USE_MEDIA_COORDINATOR) {
      await mediaCoordinator.init();

      mediaCoordinator.reportDriverStatus();
      driverState.addListener(() => mediaCoordinator.reportDriverStatus());

      // Wire shadowcam health into both matchmaker paths (USE_MESSAGE_ROUTER on/off).
      const shadowcamHealthSource = mediaCoordinator.getShadowcamPresenceMonitor();
      pavilionCoordinator.setShadowcamHealthSource(shadowcamHealthSource);
      setLegacyMatchmakerShadowcamHealthSource(shadowcamHealthSource);

      console.log('[Coordinators] ✓ MediaCoordinator initialized');
    } else {
      console.log('[Coordinators] 🚧 MediaCoordinator skipped (feature flag disabled)');
    }

    // Live connected user/admin peer count -> PlayJector (for service affinity gating).
    // Declared at function scope so the auth bridge below can resync even though the
    // manager instance is created inside the USE_MEDIA_COORDINATOR block.
    let connectedUsersIpcSyncManager: ConnectedUsersIpcSyncManager | null = null;

    // Initialize PeerCoordinator (manages peer connections and player lifecycle)
    // Subscribes to MediaCoordinator.tracksChanged for track updates to peers
    // Gated by USE_MEDIA_COORDINATOR so peer/admin/input can be disabled independently of SessionCoordinator
    if (USE_MEDIA_COORDINATOR) {
      const legacyIceState = getStateStore('ipc').iceServerState.currentState;
      const forceTurn = !legacyIceState.allowPeerConnections;

      const ipcService = serviceContainer.ipcService;

      /** Admin offer is handled before AdminPeerHandler runs; ShadowCam must be bound first or the track can stick on solid green. */
      const PLAYJECTOR_SETSOURCE_STAGGER_MS = 500;
      const SHADOWCAM_SOURCE_ACK_TIMEOUT_MS = 3_000;

      /**
       * Wait for the camera to be enumerable AND for the setSource bind to complete.
       * The camera appears in Chromium's device list before Playjector's 500ms setSource
       * stagger fires, so capturing immediately after enumeration yields the green placeholder.
       * We poll for enumeration, then apply the historical stagger delay to ensure bind.
       */
      const waitForShadowcamEnumerated = async (shadowcamName: string): Promise<void> => {
        const deadline = Date.now() + SHADOWCAM_SOURCE_ACK_TIMEOUT_MS;
        while (Date.now() < deadline) {
          await mediaCoordinator.getDeviceConfig().enumerateDevices();
          const found = mediaCoordinator
            .getDeviceConfig()
            .getShadowcams()
            .some(
              (cam) =>
                cam.displayName?.includes(shadowcamName) || cam.deviceId?.includes(shadowcamName),
            );
          if (found) {
            // Camera is enumerable but setSource (bind) fires 500ms after getSources reply.
            // Wait for that stagger plus a safety margin to avoid capturing the green placeholder.
            await new Promise<void>((resolve) => setTimeout(resolve, PLAYJECTOR_SETSOURCE_STAGGER_MS + 450));
            return;
          }
          await new Promise<void>((resolve) => setTimeout(resolve, 100));
        }
        console.warn(
          `[Coordinators] Shadowcam ${shadowcamName} not enumerated within ${SHADOWCAM_SOURCE_ACK_TIMEOUT_MS}ms — falling back to setSource stagger`,
        );
        await new Promise<void>((resolve) => setTimeout(resolve, PLAYJECTOR_SETSOURCE_STAGGER_MS + 450));
      };

      const beforeAdminMediaCapture = async (): Promise<void> => {
        ipcService.send(generators.camera.getSources());
        await waitForShadowcamEnumerated('ShadowCam03');
      };

      const activateShadowcam = async (shadowcamName: string): Promise<void> => {
        console.log(`[Coordinators] Activating shadowcam ${shadowcamName} via Playjector`);
        // Main mirrors this toggle into the sourced camera set as it passes through, so the
        // getSources reply below binds a display to this camera rather than skipping it.
        ipcService.send(generators.camera.startStopToggle(shadowcamName, true));
        ipcService.send(generators.camera.getSources());
        await waitForShadowcamEnumerated(shadowcamName);
      };

      const deactivateShadowcam = async (shadowcamName: string): Promise<void> => {
        console.log(`[Coordinators] Deactivating on-demand shadowcam ${shadowcamName}`);
        ipcService.send(generators.camera.startStopToggle(shadowcamName, false));
      };

      // Re-assert AudioCaster as default render via hub.broadcastStatus →
      // SetBroadcastActive(true) → ReassertAudioCasterDefaultEndpoint. Guest teardown
      // may have restored host speakers; game-audio capture needs AudioCaster wired again.
      const ensureAudioCasterDefault = async (): Promise<void> => {
        const hostAuthId = appCoordinator.getHostAuthId();
        console.log('[AUDIO-BUG-12009:Coordinators] Ensuring AudioCaster default via broadcastStatus(streaming)');
        ipcService.send(generators.hub.broadcastStatus('streaming', hostAuthId));
        connectedUsersIpcSyncManager?.resync();
      };

      await peerCoordinator.init(sessionCoordinator, mediaCoordinator, realtimeSignalingService, {
        iceTransportPolicy: forceTurn ? 'relay' : 'all',
        beforeAdminMediaCapture,
        activateShadowcam,
        deactivateShadowcam,
        ensureAudioCasterDefault,
      });
      const inputStores = createInputConfigStores();

      // Bridge legacy stores -> coordinator-owned stores (strangler fig)
      const legacyStore = getStateStore('ipc');
      legacyStore.gamepadState.addListener((gs) => {
        inputStores.gamepadConfig.updateState({
          autoAssign: gs.autoAssign,
          maxAssignments: gs.maxAssignments,
          shareFlags: [gs.shareGamepadOne, gs.shareGamepadTwo, gs.shareGamepadThree, gs.shareGamepadFour],
        });
      });
      legacyStore.hostRuleState.addListener((rs) => {
        inputStores.inputRules.updateState({ enableGamepadFiltering: rs.enableGamepadFiltering });
      });
      pauseInputState.addListener((ps) => {
        inputStores.pauseInput.updateState(ps);
      });

      const gamepadManager = new GamepadManager({
        ipcService,
        gamepadConfigStore: inputStores.gamepadConfig,
        inputRulesStore: inputStores.inputRules,
        appCoordinator,
      });
      const trialInput = initializeStreamTuning({ peer: peerCoordinator, ipc: ipcService,
        gamepad: gamepadManager, hostId: () => appCoordinator.getHostAuthId() });

      const activityTracker = new ActivityTracker({
        afkTimeoutMs: 10_000,
        onAfkChanged: (peerId, afk) => {
          peerCoordinator.events.afkStateChanged.emit({ peerId, afk });
        },
        onKick: (peerId) => {
          peerCoordinator.kickPeer(peerId, 'kicked:afk');
        },
      });

      const recordingControlManager = new RecordingControlManager({
        playjectorService: serviceContainer.playjectorService,
        sendIpc: (msg: unknown) => ipcService.send(msg as any),
        getHostAuthId: () => appCoordinator.getHostAuthId(),
      });
      serviceContainer.setRecordingControlManager(recordingControlManager);

      adminPeerHandler = new AdminPeerHandler({
        ipcService,
        realtimeService: serviceContainer.realtimeService,
        appCoordinator,
        peerCoordinator,
        pavilionCoordinator,
        sessionPresenceUpdater: sessionCoordinator,
        recordingControlManager,
      });

      const inputManager = new InputManager({
        peerCoordinator,
        ipcService,
        pauseInputStore: inputStores.pauseInput,
        gamepadManager,
        adminPeerHandler,
        activityTracker,
        trialInput,
      });

      peerCoordinator.registerInputStores(inputStores);
      peerCoordinator.registerGamepadManager(gamepadManager);
      peerCoordinator.registerActivityTracker(activityTracker);
      peerCoordinator.getTypeRegistry().register('admin', adminPeerHandler);

      peerCoordinator.events.dataReceived.addListener(({ peerId, data }) => {
        inputManager.handleMessage(peerId, data);
      });

      peerCoordinator.events.onPlayerLeft.addListener(({ peerId }) => {
        gamepadManager.removePeer(peerId);
        activityTracker.removePeer(peerId);
      });
      peerCoordinator.events.onAdminLeft.addListener(({ peerId }) => {
        gamepadManager.removePeer(peerId);
      });

      // Legacy playjectorHostModeEvents.ts was the only sender of hub.broadcastStatus,
      // but it's gated off when USE_MESSAGE_ROUTER is true. Playjector relies on
      // that message to run its FlushKeyboard/FlushMouse/FlushTouches/FlushGamepads
      // path on broadcast-state transitions; without this bridge, stuck inputs
      // persist across stream stops in the coordinator flow.
      new BroadcastStatusIpcSyncManager({
        ipcService,
        sessionCoordinator,
        getHostAuthId: () => appCoordinator.getHostAuthId(),
      }).start();

      // Bridge live connected user/admin peer count -> PlayJector so the service can gate
      // CPU/GPU affinity on real activity. Deliberately NOT coupled to broadcast status.
      connectedUsersIpcSyncManager = new ConnectedUsersIpcSyncManager({
        ipcService,
        peerCoordinator,
        getHostAuthId: () => appCoordinator.getHostAuthId(),
      });
      connectedUsersIpcSyncManager.start();

      // Bridge machineIdState -> truncated machine ID replication to players (strangler fig)
      const electronStores = getElectronStores();
      let currentTruncMachineId = '';

      const broadcastTruncMachineId = () => {
        if (!currentTruncMachineId) return;
        console.log('[MachineId-Bridge] Broadcasting truncMachineId to all peers');
        const msg = generators.state.replication('truncMachineIdState', currentTruncMachineId);
        peerCoordinator.broadcastToAll(JSON.stringify(msg));
      };

      electronStores.machineIdState.addListener((machineId) => {
        currentTruncMachineId = machineId.substring(6, 20);
        broadcastTruncMachineId();
      });
      if (electronStores.machineIdState.currentState) {
        currentTruncMachineId = electronStores.machineIdState.currentState.substring(6, 20);
      }

      peerCoordinator.events.onPlayerJoined.addListener(({ peerId }) => {
        console.log(`[MachineId-Bridge] onPlayerJoined: ${peerId}, truncMachineId=${currentTruncMachineId || '(empty)'}`);
        if (!currentTruncMachineId) return;
        const msg = generators.state.replication('truncMachineIdState', currentTruncMachineId);
        const sent = peerCoordinator.sendToPeer(peerId, JSON.stringify(msg));
        console.log(`[MachineId-Bridge] sendToPeer result: ${sent}`);
      });

      // Forward player-joined events to PavilionCoordinator so it can apply
      // campaign-configured matchmaking cooldowns (e.g. Hitman 30-min contract clock).
      // The coordinator is idempotent within a launch instance, so multiple joins
      // for the same launch only start the timer once.
      peerCoordinator.events.onPlayerJoined.addListener(() => {
        pavilionCoordinator.notifyPlayerJoined();
      });

      // Gate player input to the active campaign's supported input methods
      // (HostCampaignConfig.supportedInputs). Clamps PeerPermissions on join and
      // whenever the campaign config loads/clears (GitHub #13807), and notifies
      // each player client of its granted access via setInputAccess.
      const campaignInputManager = new CampaignInputManager({
        peerCoordinator,
        pavilionCoordinator,
        getHostAuthId: () => authState.currentState.hostAuthId,
      });
      campaignInputManager.init();

      console.log('[Coordinators] ✓ PeerCoordinator initialized (with InputManager, GamepadManager, AdminPeerHandler)');
    } else {
      console.log('[Coordinators] 🚧 PeerCoordinator skipped (USE_MEDIA_COORDINATOR disabled)');
    }

    // Initialize PavilionCoordinator (MVP - fully functional)
    // Pass appCoordinator to read static properties when sending HOST_CONFIG
    // Pass gameLaunchCoordinator so it can listen to campaign switch events
    // Pass sessionCoordinator so it can orchestrate stream start on match found
    pavilionCoordinator.init(
      appCoordinator,
      USE_PLAYJECTOR_COORDINATOR ? gameLaunchCoordinator : undefined,
      USE_SESSION_COORDINATOR ? sessionCoordinator : undefined
    );
    pavilionCoordinator.registerMessageHandlers(realtimeMessageRouter);
    pavilionCoordinator.registerHostMessageHandlers(realtimeMessageRouter);
    nonsoleExitCoordinator.registerMessageHandlers(realtimeMessageRouter);
    console.log('[Coordinators] ✓ PavilionCoordinator initialized');

    const sanitizationMonitor = new NonsoleSanitizationMonitor(() => sanitizationState.currentState);
    pavilionCoordinator.setSanitizationHealthSource(sanitizationMonitor);
    sanitizationState.addListener(() => sanitizationMonitor.notifyIfChanged());

    // Initialize StatefulMacroCoordinator (Phase 3 of stateful-macro-system).
    // Requires GameLaunchCoordinator's MacroManager (created during its init()),
    // so we wire it up *after* both gameLaunch and pavilion are initialized.
    if (USE_PLAYJECTOR_COORDINATOR) {
      const macroManager = (gameLaunchCoordinator as unknown as { managers: { macro: import('./game-launch/managers/MacroManager').MacroManager } }).managers.macro;
      if (macroManager) {
        statefulMacroCoordinator = new StatefulMacroCoordinator({
          realtimeService: serviceContainer.realtimeService,
          playjectorService: serviceContainer.playjectorService,
          macroManager,
          gameLaunchCoordinator,
          pavilionCoordinator,
        });
        statefulMacroCoordinator.init();
        statefulMacroCoordinator.registerMessageHandlers(realtimeMessageRouter);

        gameLaunchCoordinator.setCampaignSwitchSettleGuard(
          () => statefulMacroCoordinator?.isWaitingOutSettle() ?? false,
        );

        console.log('[Coordinators] ✓ StatefulMacroCoordinator initialized (settle guard wired)');
      } else {
        console.warn('[Coordinators] ⚠️  StatefulMacroCoordinator skipped — GameLaunchCoordinator has no MacroManager');
      }
    } else {
      console.log('[Coordinators] 🚧 StatefulMacroCoordinator skipped (USE_PLAYJECTOR_COORDINATOR disabled)');
    }

    // Initialize ScheduledScreenshotWatcher (periodic idle-screen health check).
    // Requires GameLaunchCoordinator (gameReady event), PavilionCoordinator
    // (campaign config), SessionCoordinator (stream lifecycle), and PlayjectorService.
    if (USE_PLAYJECTOR_COORDINATOR && USE_SESSION_COORDINATOR) {
      scheduledScreenshotWatcher = new ScheduledScreenshotWatcher({
        playjectorService: serviceContainer.playjectorService,
        gameLaunchCoordinator,
        pavilionCoordinator,
        sessionCoordinator,
      });
      scheduledScreenshotWatcher.init();
      wireScheduledWatcherEvents(scheduledScreenshotWatcher, pavilionCoordinator);
      console.log('[Coordinators] ✓ ScheduledScreenshotWatcher initialized');
    } else {
      console.log('[Coordinators] 🚧 ScheduledScreenshotWatcher skipped (feature flags disabled)');
    }

    // TODO-STRANGLER-FIG: Temporary adapter for authState until IdentityCoordinator is built
    // PavilionCoordinator and AppCoordinator need authId.
    // In the new architecture, IdentityCoordinator would own authState and emit events.
    authState.addListener((state) => {
      if (state.hostAuthId) {
        appCoordinator.updateHostAuthId(state.hostAuthId);
        pavilionCoordinator.updateAuthId(state.hostAuthId);
        // Re-send the current connected-user count once the host auth id is (re)bridged.
        // The initial report may have gone out with an empty/experimental id and then be
        // suppressed because the peer set hadn't changed; resync forces a fresh report with
        // the valid id.
        connectedUsersIpcSyncManager?.resync();
      }
    });
    if (authState.currentState.hostAuthId) {
      appCoordinator.updateHostAuthId(authState.currentState.hostAuthId);
      pavilionCoordinator.updateAuthId(authState.currentState.hostAuthId);
      // Resync after the initial auth-state snapshot is applied: the manager's own
      // start() already sent an initial report before this branch ran (with the empty or
      // experimental host id), so if the authenticated id is already present and the peer
      // count doesn't change, PlayJector wouldn't otherwise receive the authenticated count.
      connectedUsersIpcSyncManager?.resync();
    }

    // Initialize BeaconCoordinator (conditional)
    if (USE_BEACON_COORDINATOR && beaconCoordinator) {
      const beacon = beaconCoordinator;
      beacon.init(appCoordinator, sessionCoordinator, peerCoordinator);
      beacon.registerMessageHandlers(realtimeMessageRouter);

      // Bridge authState to BeaconCoordinator (same pattern as PavilionCoordinator)
      if (authState.currentState.hostAuthId) {
        beacon.updateAuthId(authState.currentState.hostAuthId);
      }
      authState.addListener((state) => {
        if (state.hostAuthId) {
          beacon.updateAuthId(state.hostAuthId);
        }
      });

      console.log('[Coordinators] ✓ BeaconCoordinator initialized');
    } else {
      console.log('[Coordinators] 🚧 BeaconCoordinator skipped (feature flag disabled)');
    }

    // Beacon and Pavilion share the same capture-source managers. Prime does not.
    if (USE_MEDIA_COORDINATOR && (USE_BEACON_COORDINATOR || USE_PAVILION_COORDINATOR)) {
      const legacyStores = getStateStore('ipc');
      displaySourceManager = new DisplaySourceManager({
        ipcService: serviceContainer.ipcService,
        getHostAuthId: () => appCoordinator.getHostAuthId(),
        getActiveShadowcams: () => legacyStores.activeShadowcamState.currentState.active,
      });
      displaySourceManager.registerReplicationHandler(replicationRouter);

      const cameras = mediaCoordinator.getDeviceConfig().getCameras();
      if (cameras.length > 0) {
        displaySourceManager.onCamerasChanged(cameras);
      }
      mediaCoordinator.events.deviceListChanged.addListener(() => {
        displaySourceManager?.onCamerasChanged(mediaCoordinator.getDeviceConfig().getCameras());
      });

      audioManager = new AudioManager({
        ipcService: serviceContainer.ipcService,
        getHostAuthId: () => appCoordinator.getHostAuthId(),
      });
      audioManager.registerReplicationHandler(replicationRouter);

      const deviceConfig = mediaCoordinator.getDeviceConfig();
      audioManager.updateDeviceLists(
        deviceConfig.getDesktopAudioSources(),
        deviceConfig.getMicrophones(),
      );
      mediaCoordinator.events.deviceListChanged.addListener(() => {
        const dc = mediaCoordinator.getDeviceConfig();
        audioManager?.updateDeviceLists(
          dc.getDesktopAudioSources(),
          dc.getMicrophones(),
        );
      });

      // Desktop volume stays at unity on the published track. The beacon slider
      // is intentionally not bridged to mediaCoordinator.setVolume().
      displayOutputManager = new DisplayOutputManager({
        ipcService: serviceContainer.ipcService,
        getActiveShadowcams: () => legacyStores.activeShadowcamState.currentState.active,
      });
      displayOutputManager.registerReplicationHandler(replicationRouter);

      sessionCoordinator.events.statusChanged.addListener((e) => {
        const isLive = e.status === 'hosting';
        displayOutputManager?.updateStreamStatus(isLive, isLive ? Date.now() : 0);
      });

      console.log('[Coordinators] ✓ Capture source managers initialized (DisplaySource, Audio, DisplayOutput)');
    }

    // TODO-STRANGLER-FIG: Bridge loginProfileState to AppCoordinator.authState
    loginProfileState.addListener((state) => {
      appCoordinator.updateAuthState(state.accessToken);
    });
    if (loginProfileState.currentState.accessToken) {
      appCoordinator.updateAuthState(loginProfileState.currentState.accessToken);
    }

    console.log('[Coordinators] All coordinators initialized successfully');

    restartManager = initRestartManager(playjectorMessageRouter);
  } catch (error) {
    console.error('[Coordinators] Failed to initialize coordinators:', error);
    throw error;
  }
}

// All coordinator instances are exported at their declaration site above.
